/* eslint-disable jsx-a11y/anchor-is-valid */
/**
@format
@flow
*/
import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import Squircle from '@ziynx/components/squircles';
import { SubHeading } from '@ziynx/components/typography';
import Link from '@ziynx/components/links';
import type { TextLinkType } from '@ziynx/flow-types/links';
import Colors from '@ziynx/theme/colors';
import type { Viewports } from '@ziynx/flow-types/globals';
import * as KrispyTokens from '@krispy-tokens/blue';
import CardBase from './CardBase';

export type Props = $ReadOnly<{|
  /*
   * String for talent note label
  */
  label?: string,
  /*
   * String or raw HTML containing talent notes content
  */
  content?: string,
  /*
   * String containing squircle fill.
  */
  squircleText?: string,
  /*
   * Object containing Link props.
  */
  link?: TextLinkType,
  /*
   * Boolean to disable link and see more
  */
  loading?: boolean,
  /**
   * Viewports
   */
  viewport?: Viewports,
|}>;

const ComponentWrapper = styled.div`
  box-sizing: border-box;
  padding-bottom: ${KrispyTokens.KrispySpacingElementsMdLarge}px;
  ${({ isMobile }) => isMobile && `padding-bottom: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;`}
`;

const StyledCardBase = styled(CardBase)`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  max-width: 688px;
  min-width: 288px;
  ${({ isEmpty }) => isEmpty && `
    justify-content: flex-start;
    align-items: center;
  `}
`;

const SquircleWrapper = styled.div`
  display: flex;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  padding-left: ${KrispyTokens.KrispySpacingElementsHdXlarge}px;
`;

const RenderedTextWrapper = styled.div`
  display: -webkit-box; 
  -webkit-line-clamp: 5; 
  -webkit-box-orient: vertical; 
  text-overflow: ellipsis;
  overflow: hidden;
  max-height: 120px;
  font-style: normal;
  font-weight: 400;
  max-height: 120px;
  font-family: ${KrispyTokens.KrispyTypographyFontfamily}, ${KrispyTokens.KrispyTypographyFontfallbacks};
  line-height: ${KrispyTokens.KrispyTypographyLineheightBodySmall}px;
  ${({ isExpanded }) => isExpanded && `
    max-height: 100%;
    -webkit-line-clamp:unset;
  `}
  p {
    margin-bottom: 0px;
  }
  a {
    font-size: ${KrispyTokens.KrispyTypographyFontsizeBodySmall}px;
    letter-spacing: ${KrispyTokens.KrispyTypographyLetterspacingBodySmall}px;
    font-weight:  ${KrispyTokens.KrispyTypographyFontweightBodySmallSemibold}px;
  }
`;

const StyledLink = styled(Link)`
  ${({ disabled }) => !!disabled && `
    color: ${Colors.GrayLight};
    cursor: default;
  `}
`;

const EditLinkWrapper = styled.div``;
const LinkWrapper = styled.div`
  display: block;
  ${({ seeLess, isExpanded }) => (seeLess || (!seeLess && isExpanded)) && `
    display: none;
  `}
  ${({ isExpanded, seeLess }) => seeLess && isExpanded && `
    display: block;
  `}
`;

const CardNote = (props: Props): React$Node => {
  const {
    label,
    content,
    squircleText,
    link,
    loading,
    viewport,
  } = props;

  const expandableRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [showLink, setShowLink] = useState<boolean>(false);

  useEffect(() => {
    if (expandableRef.current && expandableRef?.current?.clientHeight >= 100) {
      setShowLink(true);
    } else if (!!showLink && expandableRef.current && expandableRef?.current?.clientHeight < 100) {
      setShowLink(false);
    }
  }, [showLink, content]);

  const _handleExpand = () => {
    setIsExpanded(true);
  };

  const _handleCollapse = () => {
    setIsExpanded(false);
  };

  let isMobile = false;
  if (viewport === 'mobileSmall' || viewport === 'mobile') isMobile = true;

  // eslint-disable-next-line react/no-danger
  const _decodeContent = (htmlString: string) => (<span dangerouslySetInnerHTML={{ __html: htmlString }} style={{ overflowWrap: 'anywhere' }} />);

  /* Todo: Launch modal w/ Text Editor */
  const _renderContent = () => {
    if (!content) {
      return (
        <StyledLink
          variant="text"
          href={link?.href}
          target={link?.target}
          onClick={!loading ? link?.onClick : () => {}}
          disabled={loading}
          large
        >
          Add Note
        </StyledLink>
      );
    }
    return (
      <>
        <SubHeading large color={Colors.GrayDark}>{label}</SubHeading>
        <RenderedTextWrapper
          isExpanded={isExpanded}
          ref={expandableRef}
        >
          {_decodeContent(content)}
        </RenderedTextWrapper>
        {showLink && (
          <LinkWrapper isExpanded={isExpanded}>
            <StyledLink
              variant="text"
              onClick={!loading ? () => _handleExpand() : () => {}}
              disabled={loading}
              large
            >
              See More
            </StyledLink>
          </LinkWrapper>
        )}
        {(isExpanded || showLink) && (
          <LinkWrapper seeLess isExpanded={isExpanded}>
            <StyledLink
              variant="text"
              onClick={!loading ? () => _handleCollapse() : () => {}}
              disabled={loading}
              large
            >
              See Less
            </StyledLink>
          </LinkWrapper>
        )}
      </>
    );
  };

  return (
    <ComponentWrapper isMobile={isMobile}>
      <StyledCardBase isEmpty={!content}>
        <SquircleWrapper>
          <Squircle
            {...{ text: squircleText }}
            size={(isMobile) ? 'extraSmall' : 'medium'}
          />
        </SquircleWrapper>
        <ContentWrapper>
          {_renderContent()}
        </ContentWrapper>
        {content && (
          <EditLinkWrapper>
            <StyledLink
              variant="text"
              href={link?.href}
              target={link?.target}
              onClick={!loading ? link?.onClick : () => {}}
              disabled={loading}
              large
            >
              {link?.children}
            </StyledLink>
          </EditLinkWrapper>
        )}
      </StyledCardBase>
    </ComponentWrapper>
  );
};

export default CardNote;
